import React from 'react';
import { Box, Card, Typography, Tooltip } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';

/**
 * MetricTile — flat stat tile (docs/ui-refresh-mockup.html spec).
 * Replaces the old gradient stat cards: surface bg, 1px border, uppercase-xs
 * label, big tabular-nums value, small muted footer with optional delta.
 *
 * @param {string} label       Uppercase kicker label.
 * @param {node}   value       Big value (rendered with tabular figures).
 * @param {node}   [valueSuffix] Small suffix after the value (e.g. 'hrs', '%').
 * @param {node}   [foot]      Small footer line (e.g. '$850.00 pending').
 * @param {object} [delta]     { text: '▲ 12%', direction: 'up'|'down' } —
 *                             'up' renders good/green, 'down' crit/red.
 * @param {string} [info]      Optional tooltip text (renders an info icon by the label).
 */
export default function MetricTile({ label, value, valueSuffix, foot, delta, info, sx = {} }) {
  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        px: '15px',
        py: '14px',
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="overline" sx={{ color: 'text.faint', lineHeight: 1.4, letterSpacing: '0.06em' }}>
          {label}
        </Typography>
        {info && (
          <Tooltip title={info}>
            <InfoOutlined sx={{ fontSize: 13, color: 'text.faint' }} />
          </Tooltip>
        )}
      </Box>
      <Typography
        component="div"
        sx={{ fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.15 }}
      >
        {value}
        {valueSuffix != null && (
          <Typography component="span" sx={{ fontSize: '0.8125rem', fontWeight: 500, color: 'text.secondary', ml: 0.5 }}>
            {valueSuffix}
          </Typography>
        )}
      </Typography>
      {(foot || delta) && (
        <Typography
          component="div"
          variant="caption"
          sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}
        >
          {delta && (
            <Box
              component="span"
              sx={{ fontWeight: 600, color: delta.direction === 'down' ? 'error.main' : 'success.main' }}
            >
              {delta.text}
            </Box>
          )}
          {foot}
        </Typography>
      )}
    </Card>
  );
}
